import { LocalStorageService } from '@aiz-gl/omni-ui';
import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { lastValueFrom, map, tap } from 'rxjs';
import { AuthProfile } from '../../models/auth.model';
import { CountryCode, Region, RegionResponse } from '../../models/region.model';
import { toRecord } from '../common/common';
import {
  mapRegionResponseToModel,
  mapTokenRegionsToModel,
} from './region.mapper';

@Injectable({
  providedIn: 'root',
})
export class RegionService {
  http = inject(HttpClient);
  private localStore = inject(LocalStorageService);
  // Storage key for persisting the user's chosen region.
  // We don't need to redirect to the 'region-select' page if this is set.
  private readonly LOCAL_STORE_ACTIVE_REGION_CODE = 'atb-active-region';

  regions = signal<Region[]>([]);

  regions$ = this.http
    .get<RegionResponse[]>('auth/get-region-page-configuration')
    .pipe(
      map((response) => response.map((r) => mapRegionResponseToModel(r))),
      tap((regions) => this.regions.set(regions)),
    );

  // Provides an easy way to look up a region by code (e.g., 'US').
  // regionRecord()['US']
  regionRecord = computed(() => toRecord(this.regions(), 'code'));

  selectedRegionCode = signal<CountryCode | undefined>(undefined);

  selectedRegion = computed(() => {
    const region = this.selectedRegionCode();
    const regions = this.regionRecord();
    return region && regions[region];
  });

  selectedRegionId = computed(() => this.selectedRegion()?.id);

  // Set region code and persist to local storage.
  selectRegion(code: CountryCode) {
    this.selectedRegionCode.set(code);
    this.localStore.setItem<CountryCode>(
      this.LOCAL_STORE_ACTIVE_REGION_CODE,
      code,
    );
  }

  // Insures the region code coming from local storage is in our list of regions.
  validateRegionCode(code: CountryCode, regions: Region[]) {
    return regions.some((region) => region.code === code);
  }

  // Uses the list of regions in the auth profile to filter the full list of regions.
  // (Determine which regions the user has access to.)
  availableRegions = (authProfile?: AuthProfile) =>
    computed(() => {
      const regions = this.regions();
      return mapTokenRegionsToModel(regions, authProfile?.regions || []);
    });

  // Returns the region code that should be used for the current session.
  // If the user has not selected a region, this will return a single option or undefined.
  getValidRegionCode(regions: Region[]): CountryCode | undefined {
    // Get the persisted region code, if any.
    const localCode = this.localStore.getItem<CountryCode>(
      this.LOCAL_STORE_ACTIVE_REGION_CODE,
    );

    // Check for a persisted region code.
    const hasLocalCode = !!localCode;

    // Check if there are multiple region options available.
    const hasCodeOptions = regions.length;

    // No persisted region code, but there are multiple regions available.
    // Return undefined to prompt the user to select a region.
    if (!hasLocalCode && hasCodeOptions) return undefined;

    // Still here...validate the persisted region code.
    let valid = false;
    if (hasLocalCode && hasCodeOptions) {
      valid = this.validateRegionCode(localCode, regions);
    }
    // If valid, return the persisted code.
    if (valid) return localCode as CountryCode;

    // Failed to find a valid region code.
    return undefined;
  }

  initialize() {
    const regions = this.regions();
    // We only need to retrieve regions once per session.
    return regions.length <= 0
      ? lastValueFrom(this.regions$)
      : Promise.resolve(regions);
  }
}
